import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { EditorState } from 'prosemirror-state';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        clauseBadge: {
            createBadges: (badges: ClauseBadgeData[]) => ReturnType;
            removeBadges: () => ReturnType;
            toggleShowBadges: (show: boolean) => ReturnType;
        };
    }
}

export interface ClauseBadgeData {
    guid: string | null;
    clauseTitle: string | null;
    checklistIds: number[];
    checklistIndexes: number[];
    onChecklistClick: (checklistId: number) => void;
    onClauseClick: (clauseTitle: string | null, guid?: string | null) => void;
}

interface ClauseBadgeState {
    badges: ClauseBadgeData[];
    isVisible: boolean;
    decorationSet: DecorationSet;
}

const MAX_CHECKLIST_COUNT = 6;

const ClauseBadgeKey = new PluginKey<ClauseBadgeState>('clauseBadge');

function createBadgeDecorationWidget(endPos: number, badge: ClauseBadgeData, isMissingClause = false) {
    return Decoration.widget(
        endPos,
        () => {
            // 조항 뱃지 영역
            const badgeAreaElement = document.createElement('div');
            badgeAreaElement.className = 'clause-badge-container';
            badgeAreaElement.style.position = 'relative';
            badgeAreaElement.style.display = 'inline-block';
            badgeAreaElement.style.zIndex = '10';
            badgeAreaElement.style.width = '142px';

            // 조항 뱃지 컨테이너
            const badgeContainerElement = document.createElement('div');
            badgeContainerElement.className = 'clause-badge';
            badgeContainerElement.style.position = 'absolute';
            badgeContainerElement.style.bottom = '0px';
            badgeContainerElement.style.left = '4px';
            badgeContainerElement.style.display = 'inline-flex';
            badgeContainerElement.style.gap = '4px';
            badgeContainerElement.style.padding = '2px';
            badgeContainerElement.style.borderRadius = '4px';
            badgeContainerElement.style.border = '1px solid #ECEEF3';
            badgeContainerElement.style.backgroundColor = '#ECEEF3';
            badgeContainerElement.style.color = '#3E4048';
            badgeContainerElement.style.fontSize = '12px';
            badgeContainerElement.style.fontWeight = '600';
            badgeContainerElement.style.lineHeight = '150%';
            badgeContainerElement.style.whiteSpace = 'nowrap';
            badgeContainerElement.style.alignItems = 'start';
            badgeContainerElement.style.lineHeight = '1';
            badgeContainerElement.setAttribute('data-badge', JSON.stringify(badge));

            // 조항 뱃지 목록 컨테이너
            const badgeRowListElement = document.createElement('div');
            badgeRowListElement.className = 'clause-badge-row-list';
            badgeRowListElement.style.display = 'flex';
            badgeRowListElement.style.flexDirection = 'column';
            badgeRowListElement.style.justifyContent = 'start';

            const badgeContentFirstRowElement = document.createElement('div');
            badgeContentFirstRowElement.className = 'clause-badge-row first-row';
            badgeContentFirstRowElement.style.display = 'flex';
            badgeContentFirstRowElement.style.flexDirection = 'row';
            badgeContentFirstRowElement.style.gap = '2px';
            badgeContentFirstRowElement.style.width = '100%';

            const badgeContentSecondRowElement = document.createElement('div');
            badgeContentSecondRowElement.className = 'clause-badge-row';
            badgeContentSecondRowElement.style.display = 'flex';
            badgeContentSecondRowElement.style.flexDirection = 'row';
            badgeContentSecondRowElement.style.flexWrap = 'wrap';
            badgeContentSecondRowElement.style.gap = '2px';
            badgeContentSecondRowElement.style.width = '100%';
            badgeContentSecondRowElement.style.display = 'none';

            badgeRowListElement.appendChild(badgeContentFirstRowElement);
            if (badge.checklistIds.length > MAX_CHECKLIST_COUNT) {
                badgeRowListElement.appendChild(badgeContentSecondRowElement);
            }

            // 체크리스트 목록 버튼
            let checklistIdButtonElements: HTMLButtonElement[] = [];
            badge.checklistIds.forEach((checklistId, index) => {
                let checklistIdButtonElement = document.createElement('button');
                checklistIdButtonElement.innerHTML = `[<b style="font-weight: 600;">${badge.checklistIndexes[index]}</b>]`;
                checklistIdButtonElement.className = 'checklist-id-button';
                checklistIdButtonElement.style.padding = '0px 2px';
                if (isMissingClause) {
                    checklistIdButtonElement.style.margin = '2px 0px';
                }
                checklistIdButtonElement.style.height = '18px';
                checklistIdButtonElement.style.fontWeight = '400';
                checklistIdButtonElement.addEventListener('click', e => {
                    e.stopPropagation();
                    badge.onChecklistClick(checklistId);
                });

                checklistIdButtonElements.push(checklistIdButtonElement);
            });

            if (isMissingClause) {
                // 누락된 조항일 경우
                badgeAreaElement.style.display = 'block';
                badgeContainerElement.style.position = 'absolute';
                badgeContainerElement.style.bottom = 'auto';
                badgeContainerElement.style.top = '24px'; // 위 여백 24px
                badgeContainerElement.style.left = '24px'; // 왼쪽 여백 24px
                badgeContainerElement.style.marginLeft = '0';

                // 누락 조항 라벨
                const missingLabelElement = document.createElement('div');
                missingLabelElement.textContent = get(_)('packages.components.lib.common.tiptap.extension.clauseBadgeExtension.badgeMissingClause');
                missingLabelElement.className = 'missing-label';
                missingLabelElement.style.fontSize = '12px';
                missingLabelElement.style.fontWeight = '500';
                missingLabelElement.style.color = '#723B13';
                missingLabelElement.style.padding = '2px 8px';
                missingLabelElement.style.marginRight = '2px';
                missingLabelElement.style.borderRadius = '4px';
                missingLabelElement.style.lineHeight = '150%';
                missingLabelElement.style.backgroundColor = '#FDF6B2';

                badgeContainerElement.appendChild(missingLabelElement);
            }

            checklistIdButtonElements.forEach((button, index) => {
                if (index >= MAX_CHECKLIST_COUNT) {
                    badgeContentSecondRowElement.appendChild(button);
                } else {
                    badgeContentFirstRowElement.appendChild(button);
                }
            });

            // 체크리스트가 6개 초과되는 경우, 더보기 버튼 추가
            if (badge.checklistIds.length > MAX_CHECKLIST_COUNT) {
                const moreButtonElement = document.createElement('button');
                moreButtonElement.textContent = '...';
                moreButtonElement.className = 'checklist-more-button';
                moreButtonElement.style.display = 'block';
                moreButtonElement.style.padding = '0px 2px';

                // badgeContainerElement에 호버 이벤트 추가
                badgeAreaElement.addEventListener('mouseenter', () => {
                    moreButtonElement.style.opacity = '0';
                    badgeContentSecondRowElement.style.display = 'flex';
                });
                badgeAreaElement.addEventListener('mouseleave', () => {
                    moreButtonElement.style.opacity = '1';
                    badgeContentSecondRowElement.style.display = 'none';
                });

                badgeContentFirstRowElement.appendChild(moreButtonElement);
            }

            badgeContainerElement.appendChild(badgeRowListElement);
            badgeAreaElement.appendChild(badgeContainerElement);

            return badgeAreaElement;
        },
        { side: 1 },
    );
}

function createBadgeDecorations(doc: EditorState['doc'], badges: ClauseBadgeData[], isVisible: boolean): DecorationSet {
    if (!badges || badges.length === 0 || !isVisible) return DecorationSet.empty;

    const decorations: Decoration[] = [];

    badges.forEach(badge => {
        if (!badge.guid) {
            decorations.push(createBadgeDecorationWidget(0, badge, true));
        }

        doc.descendants((node, pos) => {
            const nodeGuid = node.attrs.guid;
            if (nodeGuid === badge.guid) {
                const endPos = pos + node.nodeSize - 1;
                const badgeDecoration = createBadgeDecorationWidget(endPos, badge);
                decorations.push(badgeDecoration);
            }
        });
    });

    return DecorationSet.create(doc, decorations);
}

export function createClauseBadgeExtensionInstance() {
    const ClauseBadgeExtension = Extension.create({
        name: 'clauseBadge',

        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: ClauseBadgeKey,
                    state: {
                        init: () => {
                            return {
                                badges: [],
                                isVisible: true,
                                decorationSet: DecorationSet.empty,
                            };
                        },
                        apply: (tr: Transaction, oldState: ClauseBadgeState) => {
                            const updatingBadgeDataMeta = tr.getMeta('badgeDataUpdated') as Partial<ClauseBadgeState> | undefined;
                            const updatingBadgeVisibilityMeta = tr.getMeta('badgeVisibilityUpdated') as Partial<ClauseBadgeState> | undefined;

                            // 뱃지 메타 없는 일반 트랜잭션: 문서 변경 시 데코레이션 위치만 매핑
                            if (!updatingBadgeDataMeta && !updatingBadgeVisibilityMeta) {
                                if (!tr.docChanged) return oldState;
                                return {
                                    ...oldState,
                                    decorationSet: oldState.decorationSet.map(tr.mapping, tr.doc),
                                };
                            }

                            const badges = updatingBadgeDataMeta?.badges ?? oldState.badges;
                            const isVisible = updatingBadgeVisibilityMeta?.isVisible ?? oldState.isVisible;

                            const decorationSet = createBadgeDecorations(tr.doc, badges, isVisible);

                            return {
                                badges: badges,
                                isVisible: isVisible,
                                decorationSet: decorationSet,
                            };
                        },
                    },
                    props: {
                        decorations: state => {
                            const pluginState = ClauseBadgeKey.getState(state);
                            if (!pluginState || !pluginState.isVisible) return DecorationSet.empty;
                            return pluginState.decorationSet;
                        },
                        handleDOMEvents: {
                            click: (view, event) => {
                                const target = event.target as HTMLElement;
                                if (target.classList.contains('clause-badge-with-checklist-ids')) {
                                    const badgeData = JSON.parse(target.getAttribute('data-badge') || '{}');
                                    if (badgeData.onClauseClick) {
                                        badgeData.onClauseClick(badgeData.clauseTitle, badgeData.guid);
                                    }
                                    return true;
                                }
                                return false;
                            },
                        },
                    },
                }),
            ];
        },

        addCommands() {
            return {
                createBadges:
                    (badges: ClauseBadgeData[]) =>
                    ({ tr, dispatch }) => {
                        tr.setMeta('badgeDataUpdated', {
                            badges,
                        });
                        tr.setMeta('badgeVisibilityUpdated', {
                            isVisible: true,
                        });
                        dispatch?.(tr);
                        return true;
                    },

                removeBadges:
                    () =>
                    ({ tr, dispatch }) => {
                        tr.setMeta('badgeDataUpdated', {
                            badges: [],
                        });
                        tr.setMeta('badgeVisibilityUpdated', {
                            isVisible: false,
                        });
                        dispatch?.(tr);
                        return true;
                    },

                toggleShowBadges:
                    (show: boolean) =>
                    ({ tr, dispatch, state }) => {
                        tr.setMeta('badgeVisibilityUpdated', {
                            isVisible: show,
                        });
                        dispatch?.(tr);
                        return true;
                    },
            };
        },
    });

    return ClauseBadgeExtension;
}
